import { Span } from '@pandiafi/design-system'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { PaymentsByMonth } from '../../../../common/generated/residentApiSchemas'
import { toShortDate, localizeCurrency } from '../utils'
import { TableTotal } from './TableTotal'
import { Divider } from '../Divider'
import { GridElement } from '../GridElement'
import { GridTable } from '../GridTable'
import { TargetTableHeader } from './TargetTableHeader'

interface TargetTableProps {
  paymentsByTarget: PaymentsByMonth['paymentsByTarget']
  openCreditNotes: PaymentsByMonth['openCreditNotes']
}

type CreditNoteRow = {
  key: string
  label: string
  date: string
  amount: string
}

const toNegativeAmount = (amount: string) =>
  (-Math.abs(Number(amount))).toFixed(2)

const toCreditNoteRows = (
  paymentsByTarget: PaymentsByMonth['paymentsByTarget'],
  openCreditNotes: PaymentsByMonth['openCreditNotes'],
  creditNoteLabel: string
): CreditNoteRow[] => {
  const appliedRows: CreditNoteRow[] = paymentsByTarget.flatMap((target) =>
    target.appliedCreditNotes.map((cn, appliedIndex) => ({
      key: `applied-${target.targetId}-${appliedIndex}`,
      label: cn.message
        ? `${creditNoteLabel} (${cn.message})`
        : creditNoteLabel,
      date: cn.date,
      amount: toNegativeAmount(cn.amount),
    }))
  )

  const openRows: CreditNoteRow[] = openCreditNotes.map((cn, openIndex) => ({
    key: `open-${openIndex}`,
    label: cn.message ? `${cn.nameOnBill} (${cn.message})` : cn.nameOnBill,
    date: cn.date,
    amount: toNegativeAmount(cn.amount),
  }))

  return [...appliedRows, ...openRows].sort((a, b) =>
    a.date.localeCompare(b.date)
  )
}

export const TargetTable: React.FC<TargetTableProps> = ({
  paymentsByTarget,
  openCreditNotes,
}) => {
  const { t } = useTranslation(['payments'])

  if (paymentsByTarget.length === 0 && openCreditNotes.length === 0)
    return <Span>{t('payments:noTargets')}</Span>

  const creditNoteRows = toCreditNoteRows(
    paymentsByTarget,
    openCreditNotes,
    t('payments:creditNote')
  )
  const sums = [
    ...paymentsByTarget.map((target) => target.expected),
    ...openCreditNotes.map((creditNote) => toNegativeAmount(creditNote.amount)),
  ]

  return (
    <>
      <TargetTableHeader />

      <Divider />

      <GridTable className="mb-4 desktop:mb-8">
        {paymentsByTarget.map((payment) => (
          <React.Fragment key={payment.targetId}>
            <GridElement columns={2}>
              <Span>{payment.nameOnBill}</Span>
            </GridElement>

            <GridElement align="right">
              <Span>{toShortDate(payment.dueDate)}</Span>
            </GridElement>

            <GridElement align="right">
              <Span>
                {payment.appliedCreditNotes.length > 0
                  ? `${localizeCurrency(Number(payment.expected))} (${localizeCurrency(
                      Number(payment.originalValue)
                    )})`
                  : localizeCurrency(Number(payment.expected))}
              </Span>
            </GridElement>
          </React.Fragment>
        ))}

        {creditNoteRows.map((row) => (
          <React.Fragment key={row.key}>
            <GridElement columns={2}>
              <Span>{row.label}</Span>
            </GridElement>

            <GridElement align="right">
              <Span>{toShortDate(row.date)}</Span>
            </GridElement>

            <GridElement align="right">
              <Span>{localizeCurrency(Number(row.amount))}</Span>
            </GridElement>
          </React.Fragment>
        ))}
      </GridTable>

      <TableTotal sums={sums} />
    </>
  )
}
